/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { IDropdownOption } from '@fluentui/react/lib/Dropdown';
import { Label } from '@fluentui/react/lib/Label';
import { Link } from '@fluentui/react/lib/Link';
import { Stack } from '@fluentui/react/lib/Stack';
import { FontWeights } from '@fluentui/react/lib/Styling';
import { Property } from 'csstype';
import { observer } from 'mobx-react';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { CalloutButton } from 'src/core/components/CalloutButton';
import { LuisDropdown } from 'src/core/components/LuisDropdown';
import { LuisText } from 'src/core/components/LuisText';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { AzureCsAccountStore } from 'src/core/stores/AzureCsAccountStore';
import { AutotagTriggerContentLeftMargin } from 'src/customText/verticals/autotagTriggerModal/autotagTriggerModal';
import { AutoLabelingModalFooter } from 'src/customText/verticals/autotagTriggerModal/components/AutoLabelingModalFooter';
import { AutoLabelingScreenProps, AutoLabelingSteps } from 'src/customText/verticals/autotagTriggerModal/types';
import { getAutoLabelingStepStatus } from 'src/customText/verticals/autotagTriggerModal/utils';
import { AOAIResourceConnector } from 'src/modules/gptPrediction/components/AOAIResourceConnector';
import styled from 'styled-components';

type ConfigureAutoLabelingJobDto = { trainedModelName?: string; azureOpenAiDeploymentName?: string; azureOpenAiResourceRegion: string };

const messages = defineMessages({
    title: { id: 'customText.configureAutoLabelingJob.title', defaultMessage: 'Configure job' },
    f1ScoreTitle: { id: 'customText.configureAutoLabelingJob.f1ScoreTitle', defaultMessage: 'F1 score' },
    azureResourceLabel: { id: 'customText.configureAutoLabelingJob.azureResourceLabel', defaultMessage: 'Language resource:' },
    trainedModelLabel: { id: 'customText.configureAutoLabelingJob.trainedModelLabel', defaultMessage: 'Choose a trained model' },
    changeAzureResource: { id: 'customText.configureAutoLabelingJob.changeAzureResource', defaultMessage: 'Change Azure resource' },
    emptyTrainedModels: {
        id: 'customText.configureAutoLabelingJob.emptyTrainedModels',
        defaultMessage: `You don't have trained models yet`
    },
    trainedModelTooltipText: {
        id: 'customText.configureAutoLabelingJob.trainedModelTooltipText',
        defaultMessage: 'Choose a trained model'
    },
    trainedModelLabelPlaceholder: {
        id: 'customText.configureAutoLabelingJob.trainedModelLabelPlaceholder',
        defaultMessage: 'Choose a trained model'
    },
    gptJobDescription: {
        id: 'customText.configureAutoLabelingJob.gptJobDescription',
        defaultMessage: `In order to use OpenAI's GPT technology to auto-label your documents, you must have an Azure OpenAI resource connected to your Language
        resource and a current deployment. Each Language resource can only have one Azure OpenAI resource connected to it. For each job you
        will also need to select your deployed OpenAI deployed model.`
    },
    trainedModelJobDescription: {
        id: 'customText.configureAutoLabelingJob.trainedModelJobDescription',
        defaultMessage: `You can use auto-labeling to allow the system to automatically label your data based on your trained model. Once you have trained a model, you can use that model to label unlabeled files.`
    }
});

const LabelContainer = styled(Label)({ display: 'flex' });

export const ConfigureAutoLabelingJob = observer((props: AutoLabelingScreenProps<ConfigureAutoLabelingJobDto>) => {
    const { autoLabelingInfoDto, onDismiss, onSubmit, onPrevious } = props;
    const { localize } = useLocalization();
    const { dispatcher, rootStore } = useGlobalState();
    const isGptJob = autoLabelingInfoDto.autoLabelingJobType === 'gpt';

    const currentResource = rootStore.azureStore.session.csAccountStore;
    const trainedModels: IDropdownOption[] = rootStore.currentCustomTextProjectStore.trainedModels.items.map(m => ({
        key: m.id,
        text: m.name,
        data: {
            secondaryText: `(${localize(messages.f1ScoreTitle)}: ${rootStore.currentCustomTextProjectStore.modelsEvaluationMap.get(m.name)
                ?.f1Score ?? '-'})`
        }
    }));

    const [selectedAzureOpenAiResource, setSelectedAzureOpenAiResource] = React.useState<AzureCsAccountStore>();
    const [selectedAzureOpenAiDeploymentId, setSelectedAzureOpenAiDeploymentId] = React.useState<string>(
        autoLabelingInfoDto.azureOpenAiDeploymentName
    );
    const [selectedTrainedModelId, setSelectedTrainedModelId] = React.useState(
        trainedModels.find(model => model.text === autoLabelingInfoDto.trainedModelLabel)?.key
    );

    const labelOnRender = (label: string, tooltipText: string | React.ReactNode, isRequired: boolean = true) => (
        <Stack horizontal verticalAlign="center">
            <LabelContainer styles={{ root: { selectors: { '::after': { paddingRight: 0 } } } }} required={isRequired}>
                {label}
            </LabelContainer>
            <CalloutButton content={tooltipText} />
        </Stack>
    );

    return (
        <Stack grow verticalAlign="space-between">
            <Stack tokens={{ childrenGap: 20, padding: `0 0 5px ${AutotagTriggerContentLeftMargin}` }}>
                <Stack tokens={{ childrenGap: 6 }}>
                    <LuisText variant="xLarge" fontWeight={FontWeights.semibold as Property.FontWeight}>
                        {localize(messages.title)}
                    </LuisText>
                    <LuisText>{localize(isGptJob ? messages.gptJobDescription : messages.trainedModelJobDescription)}</LuisText>
                </Stack>
                {isGptJob ? (
                    [
                        <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 12 }}>
                            <LuisText>{localize(messages.azureResourceLabel)}</LuisText>
                            <LuisText>{currentResource.name}</LuisText>
                            <Link onClick={() => dispatcher.dispatch('updateCsAccountSelectModalVisibility', { isVisible: true })}>
                                {localize(messages.changeAzureResource)}
                            </Link>
                        </Stack>,
                        <AOAIResourceConnector
                            dropdownWidth="50%"
                            selectedAzureOpenAiResource={selectedAzureOpenAiResource}
                            onAzureOpenAiResourceChange={setSelectedAzureOpenAiResource}
                            selectedAzureOpenAiDeploymentId={selectedAzureOpenAiDeploymentId}
                            onAzureOpenAiDeploymentIdChange={setSelectedAzureOpenAiDeploymentId}
                        />
                    ]
                ) : (
                    <LuisDropdown
                        aria-required
                        options={trainedModels}
                        styles={{ root: { width: '50%' } }}
                        selectedKey={selectedTrainedModelId}
                        ariaLabel={localize(messages.trainedModelLabel)}
                        onChange={(_, o) => setSelectedTrainedModelId(o.key)}
                        emptyStatePlaceholder={localize(messages.emptyTrainedModels)}
                        placeholder={localize(messages.trainedModelLabelPlaceholder)}
                        isLoading={rootStore.customTextProjectStoreHouse.tracker.isRunning}
                        onRenderLabel={() =>
                            labelOnRender(localize(messages.trainedModelLabel), localize(messages.trainedModelTooltipText))
                        }
                    />
                )}
            </Stack>

            <AutoLabelingModalFooter
                onDismiss={onDismiss}
                onPrevious={onPrevious}
                activeStep={AutoLabelingSteps.ConfigureJob}
                onNext={() =>
                    onSubmit({
                        azureOpenAiDeploymentName: selectedAzureOpenAiDeploymentId,
                        azureOpenAiResourceRegion: selectedAzureOpenAiResource?.region,
                        trainedModelName: trainedModels.find(model => model.key === selectedTrainedModelId)?.text
                    })
                }
                isValid={
                    getAutoLabelingStepStatus(
                        AutoLabelingSteps.ConfigureJob,
                        AutoLabelingSteps.ConfigureJob,
                        {
                            ...autoLabelingInfoDto,
                            trainedModelLabel: String(selectedTrainedModelId),
                            azureOpenAiDeploymentName: selectedAzureOpenAiDeploymentId
                        },
                        rootStore
                    ).isValid
                }
            />
        </Stack>
    );
});
